import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { TableModule, type TableLazyLoadEvent } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { CloudApiService, type McpAuditEntry, type McpAuditFilter, type McpGrant, type McpSettings } from '@/cloud/services/cloud-api.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { AssistantSettingsCardComponent } from '@/cloud/components/assistant/assistant-settings-card.component';

/**
 * AI agent access (MCP) for one namespace: the opt-in switch, how to connect an agent, and a log
 * of what agents have read. Agents authenticate with a namespace API key that has the
 * "Read via MCP" capability; nothing works until a namespace admin enables it here.
 */
@Component({
    selector: 'app-namespace-mcp',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterLink,
        ButtonModule,
        DatePickerModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        MessageModule,
        SelectModule,
        TableModule,
        TagModule,
        ToggleSwitchModule,
        TooltipModule,
        SectionCardComponent,
        EmptyStateComponent,
        AssistantSettingsCardComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-section.scss',
    template: `
        @if (namespaceId()) {
            <app-assistant-settings-card [namespaceId]="namespaceId()" [canManage]="canManage()" />
        }

        <app-section-card
            title="AI agent access (MCP)"
            subtitle="Let AI agents such as Claude read this tenant's devices, locations, logs, configuration (config files, property lists, credential names but never their values), software versions and connected equipment through the Model Context Protocol. Access is read-only."
            icon="pi pi-sparkles"
        >
            @if (settings(); as s) {
                <p-tag badge [value]="s.enabled ? 'Enabled' : 'Disabled'" [severity]="s.enabled ? 'success' : 'secondary'" [rounded]="true" />
            }
            @if (settings(); as s) {
                <div class="ns-form">
                    @if (!s.serverEnabled) {
                        <p-message severity="warn">The MCP server is switched off for this deployment. The setting below is kept but has no effect until it is switched on.</p-message>
                    }
                    <div class="ns-switch">
                        <p-toggleswitch [ngModel]="s.enabled" (ngModelChange)="setEnabled($event)" [disabled]="saving() || !canManage()" inputId="mcp-enabled" />
                        <label for="mcp-enabled">Allow AI agent access</label>
                    </div>
                    @if (s.changedUtc) {
                        <p class="ns-meta">Last changed {{ s.changedUtc | date: 'medium' }}</p>
                    }
                    @if (!canManage()) {
                        <p class="ns-meta">Only tenant admins can change this.</p>
                    }
                </div>
            } @else if (!loading()) {
                <app-empty-state icon="pi pi-exclamation-circle" tone="error" title="Could not load AI agent settings" [compact]="true" />
            }
        </app-section-card>

        @if (settings(); as s) {
            <app-section-card title="Connect an agent" subtitle="The MCP endpoint for this deployment, and how people and automations authenticate." icon="pi pi-link">
                <div class="ns-form" style="max-width: 48rem">
                    <div class="ns-field">
                        <label>Endpoint</label>
                        <div class="ns-code">
                            <code>{{ s.endpoint }}</code>
                            <p-button icon="pi pi-copy" [text]="true" [rounded]="true" pTooltip="Copy endpoint" ariaLabel="Copy endpoint" (onClick)="copy(s.endpoint)" />
                        </div>
                    </div>

                    <div class="ns-field">
                        <label>Sign in as yourself (people)</label>
                        <small
                            >Add the endpoint as a custom connector in Claude, or run the command below. The app opens a proAV.Net sign-in page where you choose which tenants it may read — only ones you have access to and that have AI agent access
                            enabled.</small
                        >
                        <div class="ns-code">
                            <pre>{{ claudeCodeSnippet() }}</pre>
                            <p-button icon="pi pi-copy" [text]="true" [rounded]="true" pTooltip="Copy command" ariaLabel="Copy command" (onClick)="copy(claudeCodeSnippet())" />
                        </div>
                    </div>

                    <div class="ns-field">
                        <label>API key (automations)</label>
                        <small> For unattended agents, create a key on the <a [routerLink]="['..', 'api-keys']">API Keys</a> tab with the <strong>Read via MCP</strong> capability — one per agent — and send it as a bearer token: </small>
                        <div class="ns-code">
                            <pre>{{ jsonSnippet() }}</pre>
                            <p-button icon="pi pi-copy" [text]="true" [rounded]="true" pTooltip="Copy configuration" ariaLabel="Copy configuration" (onClick)="copy(jsonSnippet())" />
                        </div>
                        <small>Replace &lt;YOUR_KEY&gt; with the key. Log text written by devices reaches the agent's model provider; secret-looking values are masked first.</small>
                    </div>
                </div>
            </app-section-card>

            <app-section-card title="Connected users" subtitle="People who signed in to an AI app and allowed it to read this tenant." icon="pi pi-users" [count]="grantsTotal()" [flush]="true">
                <div class="flex flex-wrap items-center gap-2 px-4 py-3">
                    <p-iconfield class="w-full md:w-80">
                        <p-inputicon class="pi pi-search" />
                        <input type="search" pInputText class="w-full" placeholder="Search name, email or app" aria-label="Search connected users" [ngModel]="grantSearch()" (ngModelChange)="onGrantSearch($event)" />
                    </p-iconfield>
                </div>
                <p-table
                    class="management-table"
                    [value]="grants()"
                    [loading]="grantsLoading()"
                    [scrollable]="true"
                    dataKey="grantId"
                    [lazy]="true"
                    [paginator]="grantsTotal() > pageSize"
                    [rows]="pageSize"
                    [first]="(grantPage() - 1) * pageSize"
                    [totalRecords]="grantsTotal()"
                    (onLazyLoad)="onGrantPage($event)"
                >
                    <ng-template #header>
                        <tr>
                            <th>User</th>
                            <th>App</th>
                            <th class="hidden md:table-cell">Connected</th>
                            <th class="hidden md:table-cell">Last used</th>
                            <th class="text-right"><span class="sr-only">Actions</span></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-grant>
                        <tr>
                            <td class="font-medium">{{ grant.userDisplayName || grant.userEmail || grant.userId }}</td>
                            <td>{{ grant.clientName }}</td>
                            <td class="hidden md:table-cell">{{ grant.createdUtc | date: 'mediumDate' }}</td>
                            <td class="hidden md:table-cell">{{ grant.lastUsedUtc ? (grant.lastUsedUtc | date: 'short') : '—' }}</td>
                            <td>
                                <div class="management-row-actions">
                                    @if (canManage()) {
                                        <p-button label="Remove" icon="pi pi-times" severity="danger" [text]="true" size="small" pTooltip="Stop this app reading this tenant" tooltipPosition="left" (onClick)="removeGrant(grant)" />
                                    }
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td colspan="5">
                                @if (grantSearch().trim()) {
                                    <app-empty-state icon="pi pi-search" title="No matching users" [description]="'Nobody matches “' + grantSearch().trim() + '”.'" [compact]="true" />
                                } @else {
                                    <app-empty-state icon="pi pi-users" title="No connected apps" description="No one has connected an app to this tenant." [compact]="true" />
                                }
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-section-card>

            <app-section-card title="Recent agent activity" subtitle="What agents have read, with the credential they used." icon="pi pi-history" [flush]="true">
                <p-button actions label="Refresh" icon="pi pi-refresh" [text]="true" size="small" (onClick)="reloadAudit()" [disabled]="auditLoading()" />
                <div class="flex flex-wrap items-center gap-2 px-4 py-3">
                    <p-select
                        class="w-full md:w-56"
                        [options]="principalOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        filterBy="label"
                        [showClear]="true"
                        placeholder="Anyone"
                        ariaLabel="Filter by who"
                        [ngModel]="auditPrincipal()"
                        (ngModelChange)="setAuditFilter({ principal: $event })"
                    />
                    <p-select
                        class="w-full md:w-52"
                        [options]="toolOptions()"
                        [filter]="true"
                        [showClear]="true"
                        [editable]="true"
                        placeholder="Any tool"
                        ariaLabel="Filter by tool"
                        [ngModel]="auditFilter().tool ?? null"
                        (ngModelChange)="setAuditFilter({ tool: $event })"
                    />
                    <p-select
                        class="w-full md:w-36"
                        [options]="outcomeOptions"
                        optionLabel="label"
                        optionValue="value"
                        [showClear]="true"
                        placeholder="Any result"
                        ariaLabel="Filter by result"
                        [ngModel]="auditFilter().outcome ?? null"
                        (ngModelChange)="setAuditFilter({ outcome: $event })"
                    />
                    <p-datepicker
                        class="w-full md:w-60"
                        selectionMode="range"
                        [readonlyInput]="true"
                        [showButtonBar]="true"
                        [maxDate]="today"
                        dateFormat="d M yy"
                        placeholder="Any date"
                        ariaLabel="Filter by date range"
                        [ngModel]="auditRange()"
                        (ngModelChange)="setAuditRange($event)"
                    />
                    @if (auditFiltered()) {
                        <p-button label="Clear filters" icon="pi pi-filter-slash" [text]="true" size="small" (onClick)="clearAuditFilters()" />
                    }
                </div>
                <p-table class="management-table" [value]="audit()" [loading]="auditLoading()" [scrollable]="true" dataKey="id">
                    <ng-template #header>
                        <tr>
                            <th>Time</th>
                            <th>Tool</th>
                            <th class="hidden md:table-cell">Credential</th>
                            <th class="hidden lg:table-cell">Arguments</th>
                            <th>Result</th>
                            <th class="hidden md:table-cell text-right">Duration</th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-entry>
                        <tr>
                            <td class="whitespace-nowrap">{{ entry.timestampUtc | date: 'short' }}</td>
                            <td>
                                <button type="button" class="ns-link-button" [pTooltip]="'Only show ' + entry.tool" (click)="setAuditFilter({ tool: entry.tool })">
                                    <code>{{ entry.tool }}</code>
                                </button>
                            </td>
                            <td class="hidden md:table-cell">
                                {{ entry.principalName || entry.principalId }}
                                @switch (entry.principalType) {
                                    @case ('Platform') {
                                        <p-tag value="Operator" severity="warn" [rounded]="true" pTooltip="A superadmin platform key — operator access that does not depend on this tenant's opt-in" />
                                    }
                                    @case ('ServiceDesk') {
                                        <p-tag value="Service desk" severity="warn" [rounded]="true" pTooltip="A support user asking the assistant across tenants — access granted by a superadmin" />
                                    }
                                    @case ('Assistant') {
                                        <p-tag value="Assistant" severity="info" [rounded]="true" pTooltip="A member of this tenant asking the in-app assistant" />
                                    }
                                }
                            </td>
                            <td class="hidden lg:table-cell">
                                <span class="ns-truncate" [pTooltip]="entry.arguments || ''">{{ entry.arguments || '—' }}</span>
                            </td>
                            <td>
                                <p-tag [value]="entry.outcome === 'ok' ? 'OK' : 'Error'" [severity]="entry.outcome === 'ok' ? 'success' : 'warn'" [rounded]="true" [pTooltip]="entry.error || ''" />
                            </td>
                            <td class="hidden md:table-cell text-right whitespace-nowrap">{{ entry.durationMs }} ms</td>
                        </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td colspan="6">
                                @if (auditFiltered()) {
                                    <app-empty-state icon="pi pi-filter" title="No activity matches these filters" [compact]="true" />
                                } @else {
                                    <app-empty-state icon="pi pi-history" title="No agent activity yet" [compact]="true" />
                                }
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
                @if (auditCursors().length > 1 || auditNext()) {
                    <div class="flex items-center justify-end gap-2 px-4 py-3">
                        <span class="text-sm text-muted-color mr-auto">Page {{ auditCursors().length }}</span>
                        <p-button label="Newer" icon="pi pi-chevron-left" [text]="true" size="small" [disabled]="auditCursors().length <= 1 || auditLoading()" (onClick)="auditNewer()" />
                        <p-button label="Older" icon="pi pi-chevron-right" iconPos="right" [text]="true" size="small" [disabled]="!auditNext() || auditLoading()" (onClick)="auditOlder()" />
                    </div>
                }
            </app-section-card>
        }
    `
})
export class NamespaceMcp {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private messageService = inject(MessageService);

    readonly pageSize = 25;
    readonly today = new Date();
    readonly outcomeOptions = [
        { label: 'OK', value: 'ok' },
        { label: 'Error', value: 'error' }
    ];

    readonly namespaceId = signal('');
    settings = signal<McpSettings | null>(null);
    loading = signal(false);
    saving = signal(false);

    // Connected users: searched and paged on the server.
    grants = signal<McpGrant[]>([]);
    grantsTotal = signal(0);
    grantPage = signal(1);
    grantSearch = signal('');
    grantsLoading = signal(false);
    private grantSearch$ = new Subject<string>();

    // Agent activity: indexed filters, newest first, cursor-paged. auditCursors[i] is the cursor that opens page i + 1.
    audit = signal<McpAuditEntry[]>([]);
    auditLoading = signal(false);
    auditFilter = signal<McpAuditFilter>({});
    auditCursors = signal<(string | null)[]>([null]);
    auditNext = signal<string | null>(null);
    private auditRequest = 0;
    private knownTools = signal<string[]>([]);
    private principals = signal<{ label: string; value: string }[]>([]);

    /** "type|id" — picking a person or key filters on both, which the API serves from one index. */
    auditPrincipal = computed(() => {
        const f = this.auditFilter();
        return f.principalType ? `${f.principalType}|${f.principalId ?? ''}` : null;
    });
    auditRange = computed<Date[] | null>(() => {
        const f = this.auditFilter();
        if (!f.from) return null;
        const end = f.to ? new Date(new Date(f.to).getTime() - 1) : null;
        return end ? [new Date(f.from), end] : [new Date(f.from)];
    });
    auditFiltered = computed(() => Object.values(this.auditFilter()).some((v) => !!v));
    principalOptions = computed(() => [
        { label: 'Any operator key', value: 'Platform|' },
        { label: 'Any service desk user', value: 'ServiceDesk|' },
        { label: 'Anyone using the assistant', value: 'Assistant|' },
        ...this.principals()
    ]);
    toolOptions = computed(() => [...new Set([...this.knownTools(), ...this.audit().map((e) => e.tool)])].sort());

    canManage = computed(() => this.api.hasCapability(this.namespaceId(), 'namespace.manage'));

    /** OAuth: Claude Code opens the proAV.Net sign-in page on first use. */
    claudeCodeSnippet = computed(() => `claude mcp add --transport http proav ${this.settings()?.endpoint ?? ''}`);

    jsonSnippet = computed(() =>
        JSON.stringify(
            {
                mcpServers: {
                    proav: {
                        type: 'http',
                        url: this.settings()?.endpoint ?? '',
                        headers: { Authorization: 'Bearer <YOUR_KEY>' }
                    }
                }
            },
            null,
            2
        )
    );

    constructor() {
        // Filters live in the URL so a filtered view ("service desk activity this week") can be shared.
        const query = this.route.snapshot.queryParamMap;
        this.grantSearch.set(query.get('users') ?? '');
        this.auditFilter.set(
            this.clean({
                tool: query.get('tool'),
                principalType: query.get('who')?.split('|')[0],
                principalId: query.get('who')?.split('|')[1],
                outcome: query.get('result') as McpAuditFilter['outcome'],
                from: query.get('from'),
                to: query.get('to')
            })
        );

        this.grantSearch$.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(() => {
            this.grantPage.set(1);
            this.syncUrl();
            this.loadGrants();
        });

        this.route.parent?.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
            this.namespaceId.set(params.get('namespaceId') ?? '');
            this.load();
        });
    }

    load() {
        if (!this.namespaceId()) return;
        this.loading.set(true);
        this.api
            .getNamespaceMcpSettings(this.namespaceId())
            .then((s) => {
                this.settings.set(s);
                this.reloadAudit();
                this.loadGrants();
                this.loadPrincipals();
            })
            .catch((err) => this.toastError('Failed to load AI agent settings', err))
            .finally(() => this.loading.set(false));
    }

    // --- Connected users ---

    onGrantSearch(value: string) {
        this.grantSearch.set(value);
        this.grantSearch$.next(value);
    }

    onGrantPage(event: TableLazyLoadEvent) {
        const page = Math.floor((event.first ?? 0) / (event.rows || this.pageSize)) + 1;
        if (page === this.grantPage() && this.grants().length) return;
        this.grantPage.set(page);
        this.loadGrants();
    }

    loadGrants() {
        if (!this.namespaceId()) return;
        this.grantsLoading.set(true);
        this.api
            .getNamespaceMcpGrants(this.namespaceId(), this.grantSearch(), this.grantPage(), this.pageSize)
            .then((page) => {
                this.grants.set(page.items);
                this.grantsTotal.set(page.total);
                this.grantPage.set(page.page);
            })
            .catch((err) => this.toastError('Failed to load connected users', err))
            .finally(() => this.grantsLoading.set(false));
    }

    removeGrant(grant: McpGrant) {
        this.api
            .removeNamespaceFromMcpGrant(this.namespaceId(), grant.grantId)
            .then(() => {
                this.grants.update((list) => list.filter((g) => g.grantId !== grant.grantId));
                this.grantsTotal.update((n) => Math.max(0, n - 1));
                this.messageService.add({ severity: 'success', summary: `${grant.clientName} can no longer read this tenant` });
                // Refill the page from the server so it does not shrink.
                if (this.grantsTotal() > this.grants().length) this.loadGrants();
            })
            .catch((err) => this.toastError('Failed to remove the app', err));
    }

    // --- Agent activity ---

    setAuditFilter(change: { principal?: string | null; tool?: string | null; outcome?: McpAuditFilter['outcome'] }) {
        const next: McpAuditFilter = { ...this.auditFilter() };
        if ('principal' in change) {
            const [type, id] = (change.principal ?? '').split('|');
            next.principalType = type || null;
            next.principalId = id || null;
        }
        if ('tool' in change) next.tool = change.tool?.trim() || null;
        if ('outcome' in change) next.outcome = change.outcome ?? null;
        this.applyAuditFilter(next);
    }

    setAuditRange(range: (Date | null)[] | null) {
        const [start, end] = range ?? [];
        // A half-picked range waits for its end date.
        if (start && !end) return;
        const from = start ? new Date(start.getFullYear(), start.getMonth(), start.getDate()) : null;
        const to = end ? new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1) : null;
        this.applyAuditFilter({ ...this.auditFilter(), from: from?.toISOString() ?? null, to: to?.toISOString() ?? null });
    }

    clearAuditFilters() {
        this.applyAuditFilter({});
    }

    reloadAudit() {
        this.auditCursors.set([null]);
        this.loadAudit();
    }

    auditOlder() {
        const next = this.auditNext();
        if (!next) return;
        this.auditCursors.update((c) => [...c, next]);
        this.loadAudit();
    }

    auditNewer() {
        if (this.auditCursors().length <= 1) return;
        this.auditCursors.update((c) => c.slice(0, -1));
        this.loadAudit();
    }

    loadAudit() {
        if (!this.namespaceId()) return;
        const request = ++this.auditRequest;
        const cursors = this.auditCursors();
        this.auditLoading.set(true);
        this.api
            .getNamespaceMcpAudit(this.namespaceId(), this.auditFilter(), cursors[cursors.length - 1], this.pageSize)
            .then((page) => {
                // Ignore a slow response to filters that have since changed.
                if (request !== this.auditRequest) return;
                this.audit.set(page.items);
                this.auditNext.set(page.nextCursor);
                this.knownTools.update((tools) => [...new Set([...tools, ...page.items.map((e) => e.tool)])]);
            })
            .catch((err) => request === this.auditRequest && this.toastError('Failed to load agent activity', err))
            .finally(() => request === this.auditRequest && this.auditLoading.set(false));
    }

    private applyAuditFilter(filter: McpAuditFilter) {
        this.auditFilter.set(this.clean(filter));
        this.syncUrl();
        this.reloadAudit();
    }

    /** People and keys for the "who" picker: connected users and this tenant's API keys. */
    private loadPrincipals() {
        const ns = this.namespaceId();
        Promise.allSettled([this.api.getNamespaceMcpGrants(ns, '', 1, 100), this.api.getNamespaceApiKeys(ns)]).then(([grants, keys]) => {
            const options = new Map<string, string>();
            if (grants.status === 'fulfilled') for (const g of grants.value.items) if (g.userId) options.set(`User|${g.userId}`, g.userDisplayName || g.userEmail || g.userId);
            if (keys.status === 'fulfilled') for (const k of keys.value as { id: string; name: string }[]) options.set(`NamespaceKey|${k.id}`, `${k.name} (API key)`);
            this.principals.set([...options].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)));
        });
    }

    private syncUrl() {
        const f = this.auditFilter();
        this.router.navigate([], {
            relativeTo: this.route,
            replaceUrl: true,
            queryParamsHandling: 'merge',
            queryParams: {
                users: this.grantSearch().trim() || null,
                tool: f.tool ?? null,
                who: f.principalType ? `${f.principalType}|${f.principalId ?? ''}` : null,
                result: f.outcome ?? null,
                from: f.from ?? null,
                to: f.to ?? null
            }
        });
    }

    private clean(filter: { [K in keyof McpAuditFilter]?: McpAuditFilter[K] | undefined }): McpAuditFilter {
        return Object.fromEntries(Object.entries(filter).filter(([, v]) => !!v)) as McpAuditFilter;
    }

    setEnabled(enabled: boolean) {
        this.saving.set(true);
        this.api
            .setNamespaceMcpEnabled(this.namespaceId(), enabled)
            .then((s) => {
                this.settings.set(s);
                this.messageService.add({ severity: 'success', summary: enabled ? 'AI agent access enabled' : 'AI agent access disabled', detail: 'Takes effect within a minute.' });
            })
            .catch((err) => {
                this.toastError('Failed to update AI agent access', err);
                // Re-render the switch at its stored position.
                this.settings.update((s) => (s ? { ...s } : s));
            })
            .finally(() => this.saving.set(false));
    }

    copy(text: string) {
        navigator.clipboard?.writeText(text);
        this.messageService.add({ severity: 'info', summary: 'Copied to clipboard' });
    }

    private toastError(summary: string, err: any) {
        const detail = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null) ?? err?.message ?? 'An error occurred';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
